/**
 * QAMessage - Reusable Q&A conversation pattern for AI chat
 *
 * Renders agent messages with quick-reply option chips.
 * When user taps a chip, it feeds back as input text.
 * Supports thinking/reasoning transitions between Q&A steps.
 *
 * Pattern: Question → Answer → Thinking/Reasoning → Next Question or Info
 *
 * Created: 2026-02-27 by Douglas + Claude
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, ChevronRight, CheckCircle2, Brain, Zap } from 'lucide-react';

/**
 * Quick-reply option chips below a question message.
 * Tapping sends the option text as user input.
 */
const QuickReplyChips = ({ options, onSelect, disabled }) => {
  const [selected, setSelected] = useState(null);

  const handleSelect = (option) => {
    if (disabled || selected) return;
    setSelected(option);
    onSelect(option);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3, duration: 0.2 }}
      className="flex flex-wrap gap-1.5 mt-2"
    >
      {options.map((option, i) => {
        const isSelected = selected === option;
        const isOther = selected && !isSelected;

        return (
          <motion.button
            key={i}
            onClick={() => handleSelect(option)}
            disabled={disabled || !!selected}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{
              opacity: isOther ? 0.3 : 1,
              scale: isSelected ? 1.02 : isOther ? 0.95 : 1,
            }}
            transition={{ delay: 0.3 + i * 0.05, duration: 0.15 }}
            className={`
              text-xs px-3 py-1.5 rounded-full border transition-all
              ${isSelected
                ? 'bg-[#AEA36D]/20 border-[#AEA36D]/50 text-[#AEA36D]'
                : isOther
                ? 'bg-white/3 border-white/5 text-white/20 cursor-default'
                : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:border-[#AEA36D]/30 hover:text-[#AEA36D] cursor-pointer'
              }
            `}
          >
            {isSelected && <CheckCircle2 size={10} className="inline mr-1 -mt-0.5" />}
            {option}
          </motion.button>
        );
      })}
    </motion.div>
  );
};

/**
 * Thinking/reasoning transition indicator.
 * Shows between Q&A steps when agent is processing.
 */
const ThinkingTransition = ({ content, step, totalSteps }) => (
  <motion.div
    initial={{ opacity: 0, height: 0 }}
    animate={{ opacity: 1, height: 'auto' }}
    exit={{ opacity: 0, height: 0 }}
    transition={{ duration: 0.3 }}
    className="font-mono text-xs py-2 pl-3 border-l-2 border-purple-500/30"
  >
    <div className="flex items-center gap-2 text-purple-400/80">
      <Brain size={12} className="animate-pulse" />
      <span>{content || 'Reasoning...'}</span>
      {step && totalSteps && (
        <span className="text-white/20 ml-auto">[{step}/{totalSteps}]</span>
      )}
    </div>
  </motion.div>
);

/**
 * Step progress indicator for multi-step Q&A flows.
 */
const StepIndicator = ({ current, total, label }) => (
  <div className="flex items-center gap-2 mb-1.5">
    <div className="flex gap-0.5">
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          className={`h-1 rounded-full transition-all duration-300 ${
            i < current
              ? 'w-4 bg-[#AEA36D]'
              : i === current
              ? 'w-4 bg-[#AEA36D]/50'
              : 'w-2 bg-white/10'
          }`}
        />
      ))}
    </div>
    {label && (
      <span className="text-[9px] text-white/30 font-mono">{label}</span>
    )}
  </div>
);

/**
 * QAMessage — Enhanced message component for Q&A conversations.
 *
 * Extends the base assistant message with:
 * - Quick-reply option chips
 * - Step progress indicator
 * - Thinking/reasoning state
 * - Transition animations
 *
 * @param {Object} msg - The message object
 * @param {string} msg.content - Message text
 * @param {string[]} [msg.options] - Quick-reply options
 * @param {boolean} [msg.isThinking] - Show as thinking/reasoning
 * @param {string} [msg.thinkingText] - Custom thinking label
 * @param {number} [msg.qaStep] - Current step in Q&A flow
 * @param {number} [msg.qaTotalSteps] - Total steps in Q&A flow
 * @param {string} [msg.qaCategory] - Category label for the step
 * @param {Function} onOptionSelect - Callback when quick-reply is tapped
 * @param {boolean} answered - Whether this question has been answered
 * @param {Object} agentInfo - Agent display info
 * @param {boolean} isError - Error state
 * @param {string} mode - 'bubble' or 'fullscreen'
 */
const QAMessage = ({
  msg,
  onOptionSelect,
  answered = false,
  agentInfo,
  isError = false,
  mode = 'bubble',
}) => {
  // Thinking/reasoning transition
  if (msg.isThinking) {
    return (
      <ThinkingTransition
        content={msg.thinkingText || msg.content}
        step={msg.qaStep}
        totalSteps={msg.qaTotalSteps}
      />
    );
  }

  const isBubbleMode = mode === 'bubble';

  return (
    <motion.div
      initial={{ opacity: 0, y: isBubbleMode ? -8 : 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className={isBubbleMode ? 'font-mono text-sm leading-relaxed py-1' : ''}
    >
      {isBubbleMode ? (
        /* Bubble mode — terminal style */
        <div className={`pl-4 border-l-2 ${isError ? 'border-red-500/50' : msg.options ? 'border-[#AEA36D]/50' : 'border-[#AEA36D]/30'}`}>
          {agentInfo && (
            <div className="flex items-center gap-1.5 mb-1 text-[10px] text-white/40">
              <span className="text-purple-400">[{agentInfo.name || 'logic'}]</span>
              {msg.qaCategory && (
                <span className="text-[#AEA36D]/50">• {msg.qaCategory}</span>
              )}
            </div>
          )}

          {/* Step progress */}
          {msg.qaStep && msg.qaTotalSteps && (
            <StepIndicator
              current={msg.qaStep - 1}
              total={msg.qaTotalSteps}
              label={msg.qaCategory}
            />
          )}

          {/* Question/content text */}
          <p className={`whitespace-pre-wrap text-sm leading-relaxed ${
            isError ? 'text-red-400' : msg.options ? 'text-white/90' : 'text-[#AEA36D]'
          }`}>
            {msg.content}
          </p>

          {/* Quick-reply chips */}
          {msg.options && msg.options.length > 0 && (
            <QuickReplyChips
              options={msg.options}
              onSelect={onOptionSelect}
              disabled={answered}
            />
          )}
        </div>
      ) : (
        /* Fullscreen mode — card style */
        <div className={`max-w-[90%] ${isError ? 'bg-red-500/10 border-red-500/30' : msg.options ? 'bg-[#AEA36D]/5 border-[#AEA36D]/20' : 'bg-white/5 border-white/10'} border rounded-2xl rounded-bl-sm px-4 py-3`}>
          {agentInfo && (
            <div className="text-[10px] text-purple-400 font-mono mb-1 flex items-center gap-1.5">
              [{agentInfo.name || 'logic'}]
              {msg.qaCategory && (
                <span className="text-[#AEA36D]/50">• {msg.qaCategory}</span>
              )}
            </div>
          )}

          {/* Step progress */}
          {msg.qaStep && msg.qaTotalSteps && (
            <StepIndicator
              current={msg.qaStep - 1}
              total={msg.qaTotalSteps}
              label={msg.qaCategory}
            />
          )}

          <p className={`text-sm whitespace-pre-wrap ${isError ? 'text-red-400' : 'text-white/90'}`}>
            {msg.content}
          </p>

          {/* Quick-reply chips */}
          {msg.options && msg.options.length > 0 && (
            <QuickReplyChips
              options={msg.options}
              onSelect={onOptionSelect}
              disabled={answered}
            />
          )}
        </div>
      )}
    </motion.div>
  );
};

export default QAMessage;
export { QuickReplyChips, ThinkingTransition, StepIndicator };
